/**
 * B2BClientSelection — Phase 37 + Phase 44.1 (Agent Client Picker + Create Client)
 *
 * Phase 44.1 additions:
 *   • "הקם לקוח חדש" button in the header (agents only).
 *   • Inline modal with the same fields as the admin client-creation form.
 *   • Auto-assigns company_id = session.agentRecord.company_id
 *     and agent_id = session.agentRecord.id — the agent cannot change these.
 *   • Calls the b2b_agent_create_client RPC (SECURITY INVOKER) which is
 *     guarded by the Phase 44.1 INSERT policy on inventory_clients.
 *   • On success the new client is appended to the local list so the agent
 *     can immediately select them without a full re-login.
 *
 * Phase 37 notes (unchanged):
 *   • Uses session.agentClientRecords (pre-loaded at login).
 *   • Falls back to a live DB query only when agentClientRecords is empty.
 *   • Supplier name shown under each client card.
 *
 * Flow:
 *   1. Reads session.agentClientRecords (populated by resolveAgentSession).
 *   2. Displays a searchable card list.
 *   3. "הקם לקוח חדש" button opens the creation modal.
 *   4. When the agent taps a client, calls setActiveBranch(record) → /b2b/catalog.
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase }   from '@/lib/supabase';
import {
  useB2BUser,
  type AgentClientRecord,
} from './B2BUserContext';
import { Loader as Loader2, CircleAlert as AlertCircle, Search, Store, ChevronLeft, Phone, Mail, Briefcase, Users, Building2, UserPlus, X, CircleCheck as CheckCircle2, User } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Pricelist {
  id:   string;
  name: string;
}

// ─── Validation helpers ───────────────────────────────────────────────────────

function validateClientForm(name: string, email: string, phone: string): string | null {
  if (!name.trim()) return 'שם הלקוח הוא שדה חובה';
  const em = email.trim();
  const ph = phone.trim();
  if (!em && !ph) return 'יש למלא לפחות אימייל או טלפון לצורך כניסה דרך OTP';
  if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) return 'כתובת אימייל אינה תקינה';
  if (ph && !/^[\d\s\-\+\(\)]{7,15}$/.test(ph)) return 'מספר טלפון אינו תקין';
  return null;
}

// ─── AgentCreateClientModal ───────────────────────────────────────────────────

interface AgentCreateClientModalProps {
  /** Agent's own inventory_agents.id — auto-assigned, cannot be changed */
  agentId:    string;
  /** Supplier company_id the client will belong to — auto-assigned */
  companyId:  string;
  /** Optional: available pricelists for this company */
  pricelists: Pricelist[];
  onClose:    () => void;
  onCreated:  (newClient: AgentClientRecord) => void;
}

function AgentCreateClientModal({
  agentId,
  companyId,
  pricelists,
  onClose,
  onCreated,
}: AgentCreateClientModalProps) {
  const [name,        setName]        = useState('');
  const [email,       setEmail]       = useState('');
  const [phone,       setPhone]       = useState('');
  const [pricelistId, setPricelistId] = useState<string | null>(null);
  const [businessId,  setBusinessId]  = useState('');
  const [saving,      setSaving]      = useState(false);
  const [formError,   setFormError]   = useState<string | null>(null);
  const [success,     setSuccess]     = useState(false);

  const handleSave = async () => {
    const err = validateClientForm(name, email, phone);
    if (err) { setFormError(err); return; }
    setFormError(null);
    setSaving(true);

    try {
      const { data, error } = await supabase.rpc('b2b_agent_create_client', {
        p_company_id:   companyId,
        p_agent_id:     agentId,
        p_name:         name.trim(),
        p_email:        email.trim()       || null,
        p_phone:        phone.trim()       || null,
        p_pricelist_id: pricelistId        || null,
        p_business_id:  businessId.trim()  || null,
      });

      if (error) throw error;

      // Build a lightweight AgentClientRecord so the parent can append it
      const newClient: AgentClientRecord = {
        id:           data as string,
        company_id:   companyId,
        name:         name.trim(),
        email:        email.trim()      || null,
        phone:        phone.trim()      || null,
        pricelist_id: pricelistId       || null,
        business_id:  businessId.trim() || null,
        client_access: 'none',
      };

      setSuccess(true);
      setTimeout(() => {
        onCreated(newClient);
        onClose();
      }, 900);
    } catch (e: unknown) {
      const msg =
        (e as { message?: string })?.message ??
        'שגיאה בשמירה — נסה שוב';
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center
                 bg-black/50 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Sheet / dialog */}
      <div
        dir="rtl"
        className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl
                   flex flex-col max-h-[92dvh] sm:max-h-[88vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4
                        border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
              <UserPlus className="w-4.5 h-4.5 text-orange-500" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">הקם לקוח חדש</h2>
              <p className="text-xs text-gray-400">הלקוח ישויך אוטומטית לסוכן שלך</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg
                       transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Success state */}
        {success && (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <CheckCircle2 className="w-14 h-14 text-green-500" />
            <p className="text-lg font-bold text-gray-800">הלקוח נוצר בהצלחה!</p>
            <p className="text-sm text-gray-500">תוכל לבחור אותו מיד מהרשימה</p>
          </div>
        )}

        {/* Form */}
        {!success && (
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">

            {/* Error */}
            {formError && (
              <div className="flex items-start gap-2 px-3 py-2.5 bg-red-50
                              border border-red-200 rounded-xl text-sm text-red-700">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{formError}</span>
              </div>
            )}

            {/* Auto-assign notice */}
            <div className="px-3 py-2.5 bg-blue-50 border border-blue-100
                            rounded-xl text-xs text-blue-700">
              הלקוח יושך אוטומטית לחשבון הסוכן שלך ולספק הנוכחי.
            </div>

            {/* Name */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                שם הלקוח / החנות <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSave()}
                placeholder="לדוגמה: רשת סופר-פארם סניף הרצליה"
                autoFocus
                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm
                           focus:outline-none focus:ring-2 focus:ring-orange-400
                           focus:border-transparent"
              />
            </div>

            {/* OTP hint */}
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100
                          rounded-xl px-3 py-2">
              יש למלא לפחות <strong>אימייל</strong> או <strong>טלפון</strong> — נדרש לכניסה ב-OTP.
            </p>

            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">אימייל</label>
              <div className="relative">
                <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4
                                 text-gray-400 pointer-events-none" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSave()}
                  placeholder="store@example.com"
                  dir="ltr"
                  className="w-full pr-9 pl-3 py-2.5 border border-gray-300 rounded-xl text-sm
                             focus:outline-none focus:ring-2 focus:ring-orange-400
                             focus:border-transparent"
                />
              </div>
            </div>

            {/* Phone */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">טלפון</label>
              <div className="relative">
                <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4
                                  text-gray-400 pointer-events-none" />
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSave()}
                  placeholder="050-0000000"
                  dir="ltr"
                  className="w-full pr-9 pl-3 py-2.5 border border-gray-300 rounded-xl text-sm
                             focus:outline-none focus:ring-2 focus:ring-orange-400
                             focus:border-transparent"
                />
              </div>
            </div>

            {/* Pricelist — loaded from the same company */}
            {pricelists.length > 0 && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  מחירון (לא חובה)
                </label>
                <select
                  value={pricelistId ?? ''}
                  onChange={e => setPricelistId(e.target.value || null)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm
                             bg-white focus:outline-none focus:ring-2 focus:ring-orange-400
                             focus:border-transparent"
                >
                  <option value="">— ללא מחירון —</option>
                  {pricelists.map(pl => (
                    <option key={pl.id} value={pl.id}>{pl.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Business ID */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-indigo-500" />
                ח.פ. / עוסק מורשה
                <span className="text-xs font-normal text-gray-400">(אופציונלי)</span>
              </label>
              <input
                type="text"
                value={businessId}
                onChange={e => setBusinessId(e.target.value)}
                placeholder="לדוגמה: 514123456"
                dir="ltr"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm
                           focus:outline-none focus:ring-2 focus:ring-indigo-400
                           focus:border-transparent"
              />
              <p className="text-xs text-gray-400">
                מספר ח.פ. לקיבוץ סניפים שונים תחת אותו עסק בפורטל ההזמנות
              </p>
            </div>
          </div>
        )}

        {/* Footer buttons */}
        {!success && (
          <div className="flex gap-3 px-5 py-4 border-t border-gray-100 shrink-0">
            <button
              onClick={onClose}
              disabled={saving}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm
                         font-medium text-gray-600 hover:bg-gray-50 transition-colors
                         disabled:opacity-50"
            >
              ביטול
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600
                         text-white text-sm font-semibold transition-colors
                         disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving
                ? <><Loader2 className="w-4 h-4 animate-spin" /> שומר…</>
                : <><UserPlus className="w-4 h-4" /> הקם לקוח</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── B2BClientSelection (main component) ─────────────────────────────────────

export function B2BClientSelection() {
  const navigate                         = useNavigate();
  const { session, setActiveBranch }     = useB2BUser();

  // ── Local state ───────────────────────────────────────────────────────────
  const [clients,       setClients]       = useState<AgentClientRecord[]>([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState<string | null>(null);
  const [search,        setSearch]        = useState('');
  const [supplierNames, setSupplierNames] = useState<Record<string, string>>({});

  // Phase 44.1 — create client modal
  const [createOpen,  setCreateOpen]  = useState(false);
  const [pricelists,  setPricelists]  = useState<Pricelist[]>([]);

  // ── Populate clients from context (or fallback DB query) ─────────────────
  const fetchClients = useCallback(async () => {
    if (!session?.agentRecord) {
      if (!session) return;
      setError(
        'פרטי הסוכן לא נמצאו — ייתכן שמדיניות ה-RLS עדיין חוסמת את הגישה. ' +
        'נסה להתנתק ולהתחבר מחדש. אם הבעיה ממשיכה, בדוק שה-SQL של שלב 38.2 הופעל בסופאבייס.',
      );
      setLoading(false);
      return;
    }

    const contextClients = session.agentClientRecords ?? [];

    // Phase 74: cascade-filter the picker by the active company when the
    // agent represents more than one supplier.  Single-company agents see
    // the full list (no cascade needed).
    const filterByActiveCompany = (list: AgentClientRecord[]): AgentClientRecord[] => {
      const companies = session.availableCompanies ?? [];
      const activeId  = session.activeCompanyId ?? null;
      if (companies.length > 1 && activeId) {
        return list.filter(c => c.company_id === activeId);
      }
      return list;
    };

    if (contextClients.length > 0) {
      setClients(filterByActiveCompany(contextClients));
      setLoading(false);
      return;
    }

    // Fallback: live DB query (hard-refresh edge case)
    setLoading(true);
    setError(null);

    const currentCompanyId = session.activeCompanyId ?? session.agentRecord.company_id;
    const agentIsMaster = (session.masterAgentCompanyIds ?? []).includes(currentCompanyId)
      || (session.agentRecord.is_master_agent && session.agentRecord.company_id === currentCompanyId);

    if (agentIsMaster) {
      // Master agent: load ALL b2b_allowed clients for the company
      const companyId = session.activeCompanyId ?? session.agentRecord.company_id;
      let { data: allData, error: allErr } = await supabase
        .from('inventory_clients')
        .select('id, company_id, name, email, phone, pricelist_id, business_id, is_vat_liable, client_access, agent_id, inventory_agents(name)')
        .eq('company_id', companyId)
        .eq('b2b_allowed', true)
        .order('name');

      // Retry without nested join if RLS on inventory_agents blocks it
      if (allErr || !allData) {
        const { data: fallbackData, error: fallbackErr } = await supabase
          .from('inventory_clients')
          .select('id, company_id, name, email, phone, pricelist_id, business_id, is_vat_liable, client_access, agent_id')
          .eq('company_id', companyId)
          .eq('b2b_allowed', true)
          .order('name');
        allData = fallbackData as unknown as typeof allData;
        allErr = fallbackErr;
      }

      if (allErr) {
        setError(allErr.message);
        setLoading(false);
        return;
      }

      const agentId = session.agentRecord.id;
      const tagged = ((allData ?? []) as Record<string, unknown>[]).map(row => ({
        id:            row.id as string,
        company_id:    row.company_id as string,
        name:          row.name as string,
        email:         (row.email as string | null) ?? null,
        phone:         (row.phone as string | null) ?? null,
        pricelist_id:  (row.pricelist_id as string | null) ?? null,
        business_id:   (row.business_id as string | null) ?? null,
        is_vat_liable: row.is_vat_liable as boolean | undefined,
        client_access: row.client_access as string | undefined,
        is_own_client: row.agent_id === agentId,
        assigned_agent_id:   (row.agent_id as string | null) ?? null,
        assigned_agent_name: ((row as Record<string, unknown>).inventory_agents as Record<string, unknown> | null)?.name as string | null ?? null,
      }));

      tagged.sort((a, b) => {
        const aOwn = a.is_own_client ? 0 : 1;
        const bOwn = b.is_own_client ? 0 : 1;
        if (aOwn !== bOwn) return aOwn - bOwn;
        return (a.name ?? '').localeCompare(b.name ?? '', 'he');
      });

      setClients(filterByActiveCompany(tagged));
    } else {
      // Regular agent: only assigned clients
      const { data, error: dbErr } = await supabase
        .from('inventory_clients')
        .select('id, company_id, name, email, phone, pricelist_id, business_id')
        .eq('agent_id', session.agentRecord.id)
        .eq('b2b_allowed', true)
        .order('name');

      if (dbErr) {
        setError(dbErr.message);
        setLoading(false);
        return;
      }

      setClients(filterByActiveCompany((data ?? []) as AgentClientRecord[]));
    }
    setLoading(false);
  }, [
    session?.agentRecord,
    session?.agentClientRecords,
    session?.availableCompanies,
    session?.activeCompanyId,
  ]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  // ── Load supplier names for the cards ────────────────────────────────────
  useEffect(() => {
    const companyIds = [...new Set(clients.map(c => c.company_id))];
    if (companyIds.length === 0) return;
    supabase
      .from('companies')
      .select('id, legal_name')
      .in('id', companyIds)
      .then(({ data }) => {
        if (!data) return;
        const map: Record<string, string> = {};
        for (const row of data) map[row.id as string] = (row.legal_name as string) ?? '';
        setSupplierNames(map);
      });
  }, [clients]);

  // ── Phase 44.1: load pricelists for the agent's primary company ───────────
  useEffect(() => {
    const companyId = session?.agentRecord?.company_id;
    if (!companyId) return;
    supabase
      .from('inventory_pricelists')
      .select('id, name')
      .eq('company_id', companyId)
      .order('name')
      .then(({ data }) => setPricelists((data ?? []) as Pricelist[]));
  }, [session?.agentRecord?.company_id]);

  // ── Filtered list ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(c =>
      c.name.toLowerCase().includes(q) ||
      (c.email ?? '').toLowerCase().includes(q) ||
      (c.phone ?? '').includes(q),
    );
  }, [clients, search]);

  // ── Select client ─────────────────────────────────────────────────────────
  const handleSelect = (client: AgentClientRecord) => {
    setActiveBranch(client);
    navigate('/b2b/catalog');
  };

  // ── Phase 44.1: handle newly created client ───────────────────────────────
  const handleClientCreated = useCallback((newClient: AgentClientRecord) => {
    // Append to local list so it's immediately selectable without re-login
    setClients(prev => {
      const exists = prev.some(c => c.id === newClient.id);
      if (exists) return prev;
      return [...prev, newClient].sort((a, b) => a.name.localeCompare(b.name, 'he'));
    });
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div dir="rtl" className="max-w-lg mx-auto px-4 py-6 space-y-5">

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-orange-50 flex items-center justify-center shrink-0">
          <Briefcase className="w-5 h-5 text-orange-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-gray-900">בחירת לקוח</h1>
          {session?.agentRecord && (
            <p className="text-xs text-gray-500">
              <span className="font-medium text-orange-600">
                חשבון סוכן: {session.agentRecord.name}
              </span>
              {session.agentRecord.email && (
                <span className="text-gray-400"> · {session.agentRecord.email}</span>
              )}
            </p>
          )}
          {/* Phase 74: Active company badge — visible only when the agent
              has more than one company.  Reinforces that the picker below
              is cascade-filtered to clients of this supplier. */}
          {(session?.availableCompanies?.length ?? 0) > 1 && session?.activeCompanyId && (() => {
            const active = session.availableCompanies
              .find(c => c.company_id === session.activeCompanyId);
            const name = active?.company_name?.trim() || '(ספק ללא שם)';
            return (
              <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                <Building2 className="w-3 h-3 text-orange-400 shrink-0" />
                <span className="font-medium text-orange-700 truncate">
                  ספק פעיל: {name}
                </span>
              </p>
            );
          })()}
          <p className="text-xs text-gray-400 mt-0.5">בחר לקוח להגשת הזמנה עבורו</p>
        </div>

        {/* Phase 44.1 — "הקם לקוח חדש" button */}
        {session?.agentRecord && (
          <button
            onClick={() => setCreateOpen(true)}
            title="הקם לקוח חדש"
            className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl
                       bg-orange-500 hover:bg-orange-600 active:scale-95
                       text-white text-xs font-semibold shadow-sm
                       transition-all"
          >
            <UserPlus className="w-4 h-4" />
            <span className="hidden sm:inline">לקוח חדש</span>
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4
                           text-gray-400 pointer-events-none" />
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="חפש לפי שם, טלפון או אימייל…"
          className="w-full pr-9 pl-4 py-2.5 rounded-xl border border-gray-200 bg-white
                     text-sm focus:outline-none focus:ring-2 focus:ring-primary-300
                     focus:border-transparent placeholder:text-gray-400"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-2.5 px-4 py-3 bg-red-50 border
                        border-red-200 rounded-2xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="text-sm">טוען רשימת לקוחות…</span>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
          <Users className="w-12 h-12 text-gray-200" />
          <p className="text-sm font-medium">
            {clients.length === 0
              ? 'אין לקוחות משויכים לסוכן זה'
              : 'לא נמצאו תוצאות לחיפוש'}
          </p>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="text-xs text-primary-600 hover:underline"
            >
              נקה חיפוש
            </button>
          )}
          {/* Prompt to create first client when list is empty */}
          {!search && clients.length === 0 && session?.agentRecord && (
            <button
              onClick={() => setCreateOpen(true)}
              className="mt-2 flex items-center gap-1.5 px-4 py-2.5 rounded-xl
                         bg-orange-500 hover:bg-orange-600 text-white text-sm
                         font-semibold transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              הקם את הלקוח הראשון
            </button>
          )}
        </div>
      )}

      {/* Client cards */}
      {!loading && !error && filtered.length > 0 && (
        <div className="space-y-2.5">
          {/* Result count */}
          <p className="text-xs text-gray-400 px-1">
            {filtered.length === clients.length
              ? `${clients.length} לקוחות`
              : `${filtered.length} מתוך ${clients.length} לקוחות`}
          </p>

          {filtered.map(client => (
            <button
              key={client.id}
              onClick={() => handleSelect(client)}
              className="w-full flex items-center gap-3 p-4 bg-white rounded-2xl
                         border border-gray-100 shadow-sm hover:shadow-md
                         hover:border-primary-200 active:scale-[.99]
                         transition-all text-right"
            >
              {/* Avatar */}
              <div className="w-12 h-12 rounded-2xl bg-primary-50 flex items-center
                              justify-center shrink-0">
                <Store className="w-6 h-6 text-primary-500" />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0 space-y-0.5">
                <div className="flex items-center gap-1.5">
                  <p className="font-semibold text-gray-900 truncate text-sm">
                    {client.name}
                  </p>
                  {/* Phase 115: ownership indicator for master agents */}
                  {session?.agentRecord?.is_master_agent && client.is_own_client === true && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5
                                     bg-teal-50 text-teal-700 text-[9px] font-bold rounded-full shrink-0">
                      <User className="w-2.5 h-2.5" />
                      לקוח שלי
                    </span>
                  )}
                  {session?.agentRecord?.is_master_agent && client.is_own_client === false && client.assigned_agent_name && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5
                                     bg-gray-100 text-gray-500 text-[9px] font-medium rounded-full shrink-0 truncate max-w-[100px]"
                          title={`סוכן: ${client.assigned_agent_name}`}>
                      <Briefcase className="w-2.5 h-2.5 shrink-0" />
                      {client.assigned_agent_name}
                    </span>
                  )}
                </div>
                {supplierNames[client.company_id] && (
                  <p className="text-[10px] text-primary-500 flex items-center gap-1">
                    <Building2 className="w-2.5 h-2.5 shrink-0" />
                    {supplierNames[client.company_id]}
                  </p>
                )}
                {client.phone && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    <span dir="ltr">{client.phone}</span>
                  </p>
                )}
                {client.email && (
                  <p className="text-xs text-gray-400 flex items-center gap-1 truncate">
                    <Mail className="w-3 h-3 shrink-0" />
                    <span className="truncate">{client.email}</span>
                  </p>
                )}
              </div>

              {/* Chevron */}
              <ChevronLeft className="w-4 h-4 text-gray-300 shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* Phase 44.1 — Create client modal (portal) */}
      {createOpen && session?.agentRecord && (
        <AgentCreateClientModal
          agentId={session.agentRecord.id}
          companyId={session.agentRecord.company_id}
          pricelists={pricelists}
          onClose={() => setCreateOpen(false)}
          onCreated={handleClientCreated}
        />
      )}

    </div>
  );
}
